import { useEffect, useRef, useState } from "react";

import { haptic } from "../lib/telegram";
import { plateLine, platesFor } from "../lib/weights";

interface Props {
  title: string;
  value: number | null;
  step: number;
  decimal: boolean;
  /**
   * Вес грифа, если блины вообще есть куда вешать. `null` — гантели, тренажёр,
   * блок: раскладка там не подсказка, а бессмыслица.
   */
  barKg?: number | null;
  /** «Далее», когда за весом сразу идут повторы; «Засчитать», когда ввод закрывает подход. */
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (value: number) => void;
  /**
   * Набранное, но не подтверждённое. Экран сохраняет его, когда человек
   * переключился на другое поле, не нажав «Далее»: раньше набранное слетало.
   */
  onDraft?: (value: number | null) => void;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

/**
 * Свой цифровой ввод.
 *
 * Системная клавиатура закрывает пол-экрана и открывается с задержкой — в
 * шестидесятисекундном окне между подходами это ощущается как тормоз, поэтому
 * §5 ТЗ требует собственный numpad. Раскладка повторяет системную, чтобы палец
 * попадал туда же, куда привык.
 */
export default function Numpad({
  title,
  value,
  step,
  decimal,
  barKg = null,
  submitLabel = "Готово",
  onClose,
  onSubmit,
  onDraft,
}: Props) {
  // Пустой буфер значит «ещё не набирали»: показываем текущее значение.
  const [buffer, setBuffer] = useState("");

  const draftRef = useRef(onDraft);
  draftRef.current = onDraft;
  useEffect(() => {
    const parsed = decimal ? parseFloat(buffer) : parseInt(buffer, 10);
    draftRef.current?.(buffer === "" || Number.isNaN(parsed) ? null : parsed);
  }, [buffer, decimal]);

  const shown = buffer !== "" ? buffer : value === null ? "" : String(value);

  // Плейт-калькулятор живёт прямо здесь: numpad и открывается тапом по весу,
  // так что отдельного жеста «покажи блины» не нужно (§5 ТЗ).
  const layout = barKg === null ? null : platesFor(Number(shown), barKg);

  function press(key: string) {
    haptic("light");
    setBuffer((prev) => {
      if (key === "del") return prev.slice(0, -1);
      if (key === ".") return decimal && !prev.includes(".") ? (prev || "0") + "." : prev;
      return prev + key;
    });
  }

  function bump(delta: number) {
    haptic("light");
    const base = buffer !== "" ? Number(buffer) || 0 : (value ?? 0);
    const next = Math.max(0, Math.round((base + delta) * 100) / 100);
    setBuffer(String(next));
  }

  function submit() {
    const parsed = decimal ? parseFloat(shown) : parseInt(shown, 10);
    if (Number.isNaN(parsed)) {
      onClose();
      return;
    }
    onSubmit(parsed);
  }

  return (
    <div className="pad" role="dialog" aria-label={title}>
      <div className="pad__top">
        <span className="pad__title">{title}</span>
        <button className="pad__step" onClick={() => bump(-step)}>
          −{step}
        </button>
        <span className="pad__num">{shown || "—"}</span>
        <button className="pad__step" onClick={() => bump(step)}>
          +{step}
        </button>
        <button className="pad__close" onClick={onClose} aria-label="Закрыть">
          ✕
        </button>
      </div>

      {barKg !== null && (
        <div className="pad__plates">
          {/* Вес грифа назван вслух: он разный у прямого, изогнутого и Смита,
              и спрятанное допущение человек унесёт в тренировку. */}
          <span className="pad__plates-key">гриф {barKg} кг</span>
          {layout === null ? (
            <span className="pad__plates--none">· легче грифа</span>
          ) : (
            <>
              <span className="pad__plates-key">· на сторону</span>
              <span className="pad__plates-val">{plateLine(layout.perSide)}</span>
              {!layout.exact && (
                <span className="pad__plates--none">
                  · нацело не собирается, выйдет {layout.achieved} кг
                </span>
              )}
            </>
          )}
        </div>
      )}

      <div className="pad__keys">
        {KEYS.map((k) => (
          <button key={k} className="pad__key" onClick={() => press(k)}>
            {k}
          </button>
        ))}
        <button
          className="pad__key"
          onClick={() => press(".")}
          disabled={!decimal}
          aria-label="Запятая"
        >
          .
        </button>
        <button className="pad__key" onClick={() => press("0")}>
          0
        </button>
        <button className="pad__key" onClick={() => press("del")} aria-label="Стереть">
          ⌫
        </button>
        <button className="pad__key pad__key--done" onClick={submit}>
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
